export interface RoundRobinStandingsEntry {
  userId: string;
  wins: number;
  losses: number;
  pointsFor: number;
  pointsAgainst: number;
  pointDifferential: number;
  matchesPlayed: number;
  rank: number;
}

export type RoundRobinTiebreakerRule = 'wins' | 'point_differential' | 'points_for' | 'seed';

export const DEFAULT_TIEBREAKERS: RoundRobinTiebreakerRule[] = [
  'wins',
  'point_differential',
  'points_for',
  'seed'
];

type StandingsEntryMap = Map<string, RoundRobinStandingsEntry>;

export function calculateStandings(
  players: Array<{ userId: string; seed: number | null | undefined }>,
  matches: Array<{ playerA: { userId: string }; playerB: { userId: string }; result?: { aScore: number; bScore: number; winnerUserId: string; playerAPoints?: number; playerBPoints?: number } | null }>,
  tiebreakers: RoundRobinTiebreakerRule[] = DEFAULT_TIEBREAKERS
): RoundRobinStandingsEntry[] {
  const standings = buildInitialStandings(players);
  const seedByUser = new Map<string, number>();
  for (const player of players) {
    const safeSeed = Number.isFinite(player.seed ?? NaN) ? (player.seed as number) : Number.MAX_SAFE_INTEGER;
    seedByUser.set(player.userId, safeSeed);
  }

  for (const match of matches) {
    const result = match.result;
    if (!result) continue;

    const aEntry = standings.get(match.playerA.userId);
    const bEntry = standings.get(match.playerB.userId);
    if (!aEntry || !bEntry) continue;

    console.log('[calculateStandings] Processing match result:', {
      playerA_id: match.playerA.userId,
      playerB_id: match.playerB.userId,
      aScore: result.aScore,
      bScore: result.bScore,
      winnerUserId: result.winnerUserId
    });

    aEntry.matchesPlayed++;
    bEntry.matchesPlayed++;

    const aPointsFor = (typeof result.playerAPoints === 'number' && Number.isFinite(result.playerAPoints)) ? result.playerAPoints : (result.aScore ?? 0);
    const bPointsFor = (typeof result.playerBPoints === 'number' && Number.isFinite(result.playerBPoints)) ? result.playerBPoints : (result.bScore ?? 0);
    aEntry.pointsFor += aPointsFor;
    aEntry.pointsAgainst += bPointsFor;
    bEntry.pointsFor += bPointsFor;
    bEntry.pointsAgainst += aPointsFor;

    aEntry.pointDifferential = aEntry.pointsFor - aEntry.pointsAgainst;
    bEntry.pointDifferential = bEntry.pointsFor - bEntry.pointsAgainst;

    console.log('[calculateStandings] Standings updated:', {
      playerA: { userId: aEntry.userId, pointsFor: aEntry.pointsFor, pointsAgainst: aEntry.pointsAgainst },
      playerB: { userId: bEntry.userId, pointsFor: bEntry.pointsFor, pointsAgainst: bEntry.pointsAgainst }
    });

    const winnerId = resolveWinner(result, match.playerA.userId, match.playerB.userId);
    if (winnerId === aEntry.userId) {
      aEntry.wins++;
      bEntry.losses++;
    } else if (winnerId === bEntry.userId) {
      bEntry.wins++;
      aEntry.losses++;
    }
  }

  const sorted = Array.from(standings.values()).sort((a, b) => compareEntries(a, b, tiebreakers, seedByUser));

  sorted.forEach((entry, index) => {
    entry.rank = index + 1;
  });

  return sorted;
}

function buildInitialStandings(players: Array<{ userId: string }>): StandingsEntryMap {
  const map = new Map<string, RoundRobinStandingsEntry>();
  for (const player of players) {
    map.set(player.userId, {
      userId: player.userId,
      wins: 0,
      losses: 0,
      pointsFor: 0,
      pointsAgainst: 0,
      pointDifferential: 0,
      matchesPlayed: 0,
      rank: 0
    });
  }
  return map;
}

function resolveWinner(result: { aScore: number; bScore: number; winnerUserId: string }, playerAId: string, playerBId: string): string {
  if (result.winnerUserId === playerAId || result.winnerUserId === playerBId) {
    return result.winnerUserId;
  }
  return result.aScore >= result.bScore ? playerAId : playerBId;
}

function compareEntries(
  a: RoundRobinStandingsEntry,
  b: RoundRobinStandingsEntry,
  tiebreakers: RoundRobinTiebreakerRule[],
  seedByUser: Map<string, number>
): number {
  for (const rule of tiebreakers) {
    const diff = compareByRule(rule, a, b, seedByUser);
    if (diff !== 0) return diff;
  }
  return 0;
}

function compareByRule(
  rule: RoundRobinTiebreakerRule,
  a: RoundRobinStandingsEntry,
  b: RoundRobinStandingsEntry,
  seedByUser: Map<string, number>
): number {
  switch (rule) {
    case 'wins':
      return b.wins - a.wins;
    case 'point_differential':
      return b.pointDifferential - a.pointDifferential;
    case 'points_for':
      return b.pointsFor - a.pointsFor;
    case 'seed':
      const aSeed = seedByUser.get(a.userId) ?? Number.MAX_SAFE_INTEGER;
      const bSeed = seedByUser.get(b.userId) ?? Number.MAX_SAFE_INTEGER;
      return aSeed - bSeed;
  }
}
